<template>
  <div>
    <div class="d-flex justify-space-between align-center gap-2">
      <v-text-field
        v-model="search"
        label="Buscar"
        single-line
        hide-details
        outlined
        dense
      />

      <v-btn
        icon
        @click="exportToExcel"
        :disabled="loading || loadingExport"
        :loading="loadingExport"
      >
        <v-icon>mdi-file-excel</v-icon>
      </v-btn>
      <v-btn
        icon
        @click="getTickets"
        :disabled="temporaryDisabled"
        :loading="loading"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-alert v-if="error" type="error">
      {{ error }}
      <v-btn @click="getTickets">Tentar novamente</v-btn>
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="tickets"
      :loading="loading"
      :search="search"
      sort-by="createdAt"
      sort-desc
    >
      <template v-slot:item.id="{ item }">
        <span class="text-overline">#{{ item.id | shortId }}</span>
      </template>
      <template v-slot:item.amount="{ item }">
        {{ item.amount | currency }}
      </template>
      <template v-slot:item.date="{ item }">
        {{ item.date | date }}
      </template>
      <template v-slot:item.TicketBlock.TicketGroup.name="{ item }">
        {{ item.TicketBlock.TicketGroup.name }}
        <small class="d-block">
          {{ item.TicketBlock.name }}
        </small>
      </template>
      <template v-slot:item.PaymentpaymentMethod="{ item }">
        {{
          PAYMENT.paymentMethod[item.Payment.paymentMethod]?.text ||
          item.Payment.paymentMethod
        }}
      </template>

      <template v-slot:item.Payment.paymentType="{ item }">
        {{
          PAYMENT.paymentType[item.Payment.paymentType]?.text ||
          item.Payment.paymentType
        }}
        <v-chip
          x-small
          class="px-2"
          v-if="item.Payment.paymentType === 'CREDIT_CARD'"
        >
          {{ item.Payment.installments === 1 ? "à vista" : item.Payment.installments + "x" }}
        </v-chip>
      </template>

      <template v-slot:item.Payment.status="{ item }">
        <v-chip
          :color="PAYMENT.ticketStatus[item.Payment.status]?.color || 'grey'"
          small
          label
          class="w-full justify-center px-2"
        >
          {{ PAYMENT.ticketStatus[item.Payment.status]?.text || item.Payment.status }}
        </v-chip>
      </template>
      <template v-slot:item.createdAt="{ item }">
        {{ item.createdAt | date("DD/MM/YYYY HH:mm") }}
      </template>
      <template v-slot:no-data>
        Nenhum pagamento encontrado para este evento.
      </template>
    </v-data-table>
  </div>
</template>

<script>
import PARTY from "@/services/admin/parties";
import PAYMENT from "@/utils/payment";
import ExcelJS from "exceljs";
import moment from "moment";

export default {
  name: "PartyTickets",
  props: {
    party: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      tickets: [],
      loadingExport: false,
      temporaryDisabled: false,
      headers: [
        { text: "ID", value: "id" },
        { text: "Valor", value: "amount" },
        { text: "Setor", value: "TicketBlock.TicketGroup.name" },
        { text: "Método de Pagamento", value: "Payment.paymentMethod" },
        { text: "Tipo de Pagamento", value: "Payment.paymentType" },
        { text: "Status", value: "Payment.status" },
        { text: "Data", value: "createdAt", align: "end" },
      ],
      search: "",
      PAYMENT,
    };
  },
  methods: {
    async getTickets() {
      this.loading = true;
      this.error = null;
      try {
        const data = await PARTY.tickets(this.party.id);
        this.tickets = data.tickets;

        this.disableButton(3000);
      } catch (error) {
        console.error("Erro ao buscar ingressos:", error);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    disableButton(time) {
      this.temporaryDisabled = true;
      setTimeout(() => {
        this.temporaryDisabled = false;
      }, time);
    },
    async exportToExcel() {
      this.loadingExport = true;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Pagamentos");
      // Adiciona os cabeçalhos das colunas
      worksheet.columns = [
        { header: "ID", key: "id", width: 10 },
        { header: "Valor", key: "amount", width: 15 },
        { header: "Taxa de Cliente", key: "clientFee", width: 15 },
        { header: "Taxa da Plataforma", key: "platformFee", width: 15 },
        { header: "Setor", key: "ticketGroup", width: 15 },
        { header: "Bloco", key: "ticketBlock", width: 15 },
        { header: "ID Pagamento", key: "paymentId", width: 20 },
        { header: "Método de Pagamento", key: "paymentMethod", width: 20 },
        { header: "Tipo de Pagamento", key: "paymentType", width: 20 },
        { header: "Parcelas", key: "installments", width: 10 },
        { header: "Vendedor", key: "seller", width: 20 },
        { header: "Status", key: "status", width: 20 },
        { header: "Data", key: "createdAt", width: 20 },
      ];

      // Adiciona os dados dos pagamentos
      this.tickets.forEach((ticket) => {
        const payment = ticket.Payment;

        const row = worksheet.addRow({
          id: `#${ticket.id.slice(0, 8)}`,
          amount: ticket.amount,
          clientFee: ticket.clientFee || 0,
          platformFee: ticket.platformFee || 0,
          paymentId: payment.id,
          paymentMethod:
            PAYMENT.paymentMethod[payment.paymentMethod]?.text ||
            payment.paymentMethod,
          paymentType:
            PAYMENT.paymentType[payment.paymentType]?.text ||
            payment.paymentType,
          installments: payment.installments || 1,
          ticketGroup: ticket.TicketBlock.TicketGroup.name,
          ticketBlock: ticket.TicketBlock.name,
          seller: payment.PosSession?.name || null,
          status: PAYMENT.ticketStatus[payment.status]?.text || payment.status,
          createdAt: moment(ticket.createdAt).format("DD/MM/YYYY HH:mm"),
        });

        row.getCell("amount").numFmt = "R$ #,##0.00";
        row.getCell("clientFee").numFmt = "R$ #,##0.00";
        row.getCell("platformFee").numFmt = "R$ #,##0.00";
        row.getCell("installments").numFmt = "#";
        row.getCell("createdAt").numFmt = "DD/MM/YYYY HH:mm";
      });

      // Salva o arquivo Excel
      const excelBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      const name = `ingress-${this.normalizeName(
        this.party.name
      )}-${moment().format("DD-MM-YYYY-HH-mm")}.xlsx`;
      setTimeout(() => {
        this.saveAs(blob, name);
        this.loadingExport = false;
      }, 1000);
    },
    saveAs(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    },
    normalizeName(name) {
      return name.replace(/ /g, "_");
    },
  },
  mounted() {
    this.getTickets();
  },
};
</script>
